/**
 * ReminderAudienceListModal
 * --------------------------
 * Read-only drawer that shows the EXACT recipient list for a given
 * auto-reminder cohort (7d or 24h) of a webinar — plus every skip-
 * bucket (opt-out, attended, registered, already-reminded, …) so the
 * admin can vet who'll get the next automatic mail before the job
 * fires.
 *
 * Backend: GET /api/admin/webinars/{id}/reminder-audience-list?cohort=7d|24h
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Mail, Search, CheckCircle2, MinusCircle } from "lucide-react";

const COLLECTION_LABEL = {
  leads: "Lead",
  contact_requests: "Kontakt",
  customer_accounts: "Kunde",
  partners: "Partner",
};

const BUCKETS_7D = [
  { key: "eligible",         label: "Bekommt heute die Mail",   tone: "ok"   },
  { key: "registered",       label: "Schon bei Zoho registriert", tone: "skip" },
  { key: "already_clicked",  label: "Hat den Invite-Link bereits geklickt", tone: "skip" },
  { key: "already_reminded", label: "Hat den Reminder schon bekommen", tone: "skip" },
  { key: "attended",         label: "War an einem früheren Webinar", tone: "skip" },
  { key: "opt_out",          label: "Opt-Out / Demo / abgelehnt", tone: "skip" },
];

const BUCKETS_24H = [
  { key: "eligible",         label: "Bekommt die Mail",          tone: "ok"   },
  { key: "already_reminded", label: "Hat den 24h-Reminder schon bekommen", tone: "skip" },
  { key: "attended",         label: "War an einem früheren Webinar", tone: "skip" },
  { key: "opt_out",          label: "Opt-Out / Demo / abgelehnt / inaktiv", tone: "skip" },
];

export default function ReminderAudienceListModal({ webinar, apiBase, token, initialCohort = "7d", onClose }) {
  const [cohort, setCohort] = useState(initialCohort);
  const [loading, setLoading] = useState(false);
  const [audience, setAudience] = useState(null);
  const [filter, setFilter] = useState("");

  const buckets = cohort === "7d" ? BUCKETS_7D : BUCKETS_24H;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setAudience(null);
    axios
      .get(`${apiBase}/admin/webinars/${webinar.id}/reminder-audience-list`, {
        params: { cohort },
        headers: { "X-Admin-Token": token },
      })
      .then((r) => { if (!cancelled) setAudience(r.data?.audience || null); })
      .catch(() => { if (!cancelled) toast.error("Liste konnte nicht geladen werden."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [apiBase, token, webinar.id, cohort]);

  const filtered = useMemo(() => {
    if (!audience) return {};
    const q = filter.trim().toLowerCase();
    const out = {};
    for (const { key } of buckets) {
      const list = audience[key] || [];
      out[key] = q
        ? list.filter((c) =>
            (c.name || "").toLowerCase().includes(q) ||
            (c.email || "").toLowerCase().includes(q)
          )
        : list;
    }
    return out;
  }, [audience, filter, buckets]);

  const eligibleCount = audience?.eligible?.length ?? 0;
  const alreadyDispatched = audience?.already_dispatched;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      data-testid="reminder-audience-list-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="al-card max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-slate-200 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold text-violet-700">
              Auto-Reminder · Empfängerliste
            </div>
            <h2 className="text-lg font-bold text-slate-900 mt-1 leading-tight truncate">{webinar.title}</h2>
            <div className="text-xs text-slate-500 mt-0.5">
              Alle Kontakte, die durch den Filter laufen — sortiert nach Status.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="reminder-audience-close"
            className="ml-auto p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Cohort switch + filter */}
        <div className="px-4 sm:px-5 pt-4 pb-2 flex flex-wrap items-center gap-2 border-b border-slate-100">
          <div className="inline-flex rounded-lg bg-slate-100 p-0.5">
            {["7d", "24h"].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCohort(c)}
                data-testid={`reminder-audience-cohort-${c}`}
                className={`px-3 py-1.5 text-xs font-bold rounded-md transition ${
                  cohort === c ? "bg-white text-violet-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {c === "7d" ? "7 Tage vorher" : "24 h vorher"}
              </button>
            ))}
          </div>

          <div className="relative ml-auto w-full sm:w-64">
            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filtern nach Name / E-Mail"
              data-testid="reminder-audience-filter"
              className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-slate-200 focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none"
            />
          </div>
        </div>

        {/* Headline */}
        <div className="px-4 sm:px-5 pt-3 pb-1">
          {alreadyDispatched && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
              ⚠ Dieser Reminder wurde bereits versendet — die Liste unten ist hypothetisch (Kontrollansicht).
            </div>
          )}
          <div className="text-sm text-slate-700">
            <span className="font-bold text-slate-900">{eligibleCount}</span>{" "}
            {eligibleCount === 1 ? "Empfänger bekommt" : "Empfänger bekommen"} die{" "}
            <span className="font-bold">{cohort === "7d" ? "7-Tage" : "24-Stunden"}-Reminder-Mail</span>{" "}
            für dieses Webinar.
          </div>
        </div>

        <div className="px-4 sm:px-5 pb-5 pt-2 overflow-y-auto flex-1 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Liste …
            </div>
          )}

          {!loading && audience && buckets.map(({ key, label, tone }) => {
            const list = filtered[key] || [];
            if (!list.length) return null;
            const isOk = tone === "ok";
            return (
              <section key={key} data-testid={`reminder-bucket-${key}`}>
                <header className={`flex items-center gap-2 mb-1.5 text-xs font-bold uppercase tracking-wider ${
                  isOk ? "text-emerald-700" : "text-slate-500"
                }`}>
                  {isOk ? <CheckCircle2 className="h-3.5 w-3.5" /> : <MinusCircle className="h-3.5 w-3.5" />}
                  <span>{label}</span>
                  <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold ${
                    isOk ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"
                  }`}>
                    {list.length}
                  </span>
                </header>
                <ul className="divide-y divide-slate-100 border border-slate-100 rounded-lg overflow-hidden">
                  {list.map((c) => (
                    <li
                      key={`${c.collection}-${c.id}-${c.email}`}
                      className={`px-3 py-2 flex items-center gap-2 ${
                        isOk ? "bg-white hover:bg-emerald-50/40" : "bg-slate-50/40"
                      }`}
                      data-testid={`reminder-recipient-${c.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className={`text-sm font-semibold truncate ${isOk ? "text-slate-900" : "text-slate-600"}`}>
                          {c.name || "—"}
                        </div>
                        <div className="text-[11px] text-slate-500 font-mono truncate">{c.email}</div>
                      </div>
                      <span className="text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-slate-100 text-slate-600 border border-slate-200 shrink-0">
                        {COLLECTION_LABEL[c.collection] || c.collection}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}

          {!loading && audience && eligibleCount === 0 && (filtered.eligible || []).length === 0 && !filter && (
            <div className="text-center py-10 text-slate-400 text-sm">
              <Mail className="h-6 w-6 mx-auto mb-2 opacity-40" />
              Aktuell bekommt niemand diese Reminder-Mail — alle Kontakte sind gefiltert (registriert, opt-out, schon erinnert oder bereits Teilnehmer).
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
